import type { AppNotification } from "types/Notification";
import type { BudgetTransaction } from "types/BudgetTransaction";
import type { GroupSummary, UserEntry } from "types/Group";
import type { GroupTransaction } from "types/GroupTransaction";
import { formatCurrencyAmountOrMasked } from "shared/money/balances";
import { buildCardStatementSummaries, formatStatementTotals } from "features/budget";
import { toTimestamp } from "utils/dateUtils";
import {
    PERSONAL_UPCOMING_PAYMENT_RANKING,
    rankGroupActivity,
    rankPersonalPayment,
    type PersonalUpcomingPaymentRanking,
    type PersonalUpcomingPaymentRankingConfig,
} from "lib/guideRanking";

const DAY_MS = 24 * 60 * 60 * 1000;
const GROUP_NOTIFICATION_WINDOW_DAYS = 14;

function startOfDay(value: Date): Date {
    const copy = new Date(value);
    copy.setHours(0, 0, 0, 0);
    return copy;
}

function daysUntil(date: string | Date, now = new Date()): number {
    const target = startOfDay(new Date(date)).getTime();
    const current = startOfDay(now).getTime();
    return Math.round((target - current) / DAY_MS);
}

function isSpanishLocale(locale: string): boolean {
    return locale.toLowerCase().startsWith("es");
}

function humanPaymentTitle(days: number, locale: string, transactionName: string): string {
    const safeName = transactionName.trim() || (isSpanishLocale(locale) ? "El movimiento" : "This payment");
    if (isSpanishLocale(locale)) {
        if (days < 0) {
            const overdueDays = Math.abs(days);
            return `${safeName} venció hace ${overdueDays} ${overdueDays === 1 ? "día" : "días"}`;
        }
        if (days === 0) return "Hoy debes pagar";
        if (days === 1) return "Mañana debes pagar";
        return `En ${days} dias debes pagar`;
    }
    if (days < 0) {
        const overdueDays = Math.abs(days);
        return `${safeName} was due ${overdueDays} ${overdueDays === 1 ? "day" : "days"} ago`;
    }
    if (days === 0) return "Pay today";
    if (days === 1) return "Pay tomorrow";
    return `Pay in ${days} days`;
}

function humanCardPaymentTitle(days: number, locale: string, cardLabel: string): string {
    const card = cardLabel.trim() || (isSpanishLocale(locale) ? "la tarjeta" : "your card");
    if (isSpanishLocale(locale)) {
        if (days < 0) {
            const overdueDays = Math.abs(days);
            return `${card} venció hace ${overdueDays} ${overdueDays === 1 ? "día" : "días"}`;
        }
        if (days === 0) return `Pagá ${card} hoy`;
        if (days === 1) return `Pagá ${card} mañana`;
        return `Pagá ${card} en ${days} días`;
    }
    if (days < 0) {
        const overdueDays = Math.abs(days);
        return `${card} was due ${overdueDays} ${overdueDays === 1 ? "day" : "days"} ago`;
    }
    if (days === 0) return `Pay ${card} today`;
    if (days === 1) return `Pay ${card} tomorrow`;
    return `Pay ${card} in ${days} days`;
}

export function buildCardStatementDueNotifications({
    transactions,
    balancesVisible,
    locale,
    max,
    now = new Date(),
    ranking,
}: {
    transactions: BudgetTransaction[];
    balancesVisible: boolean;
    locale: string;
    max?: number;
    now?: Date;
    ranking?: PersonalUpcomingPaymentRanking;
}): AppNotification[] {
    const config: PersonalUpcomingPaymentRankingConfig = { ...PERSONAL_UPCOMING_PAYMENT_RANKING, ...ranking };
    const today = startOfDay(now).getTime();
    const minDate = today - config.overdueWindowDays * DAY_MS;
    const maxDate = today + config.lookAheadDays * DAY_MS;
    const maxItems = max ?? config.maxShown;

    return buildCardStatementSummaries(transactions)
        .filter((summary) => !summary.isPaid)
        .map((summary) => ({
            summary,
            dueDays: daysUntil(summary.dueDateParsed, now),
        }))
        .filter(({ summary, dueDays }) => {
            const dueDay = startOfDay(summary.dueDateParsed).getTime();
            return dueDay >= minDate && dueDay <= maxDate;
        })
        .map((candidate) => ({
            ...candidate,
            score: rankPersonalPayment(
                {
                    _id: candidate.summary.importId,
                    name: candidate.summary.label,
                    amount: Object.values(candidate.summary.totalsByCurrency)[0] || 0,
                    payer: "",
                    date: candidate.summary.dueDate,
                    currency: Object.keys(candidate.summary.totalsByCurrency)[0] || "ARS",
                    type: "expense",
                    splitDetails: {},
                },
                candidate.dueDays,
                config,
            ) + (candidate.dueDays <= 3 ? 500 : 0),
        }))
        .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.summary.dueDateParsed.getTime() - b.summary.dueDateParsed.getTime();
        })
        .slice(0, maxItems)
        .map(({ summary, dueDays }) => {
            const isOverdue = dueDays < 0;
            const body = formatStatementTotals(summary.totalsByCurrency, balancesVisible, locale);
            return {
                id: `card-statement-due-${summary.importId}`,
                scope: "personal",
                tone: isOverdue || dueDays <= 3 ? "warning" : "info",
                title: humanCardPaymentTitle(dueDays, locale, summary.label),
                body,
                meta: summary.dueDateParsed.toLocaleDateString(locale, { day: "numeric", month: "short" }),
                date: summary.dueDateParsed.toISOString(),
                actionLabel: isSpanishLocale(locale) ? "Ver en calendario" : "View calendar",
                sourceId: summary.importId,
            } satisfies AppNotification;
        });
}

export function buildUpcomingPersonalExpenseNotifications({
    transactions,
    balancesVisible,
    locale,
    max,
    now = new Date(),
    ranking,
}: {
    transactions: BudgetTransaction[];
    balancesVisible: boolean;
    locale: string;
    max?: number;
    now?: Date;
    ranking?: PersonalUpcomingPaymentRanking;
}): AppNotification[] {
    const config: PersonalUpcomingPaymentRankingConfig = { ...PERSONAL_UPCOMING_PAYMENT_RANKING, ...ranking };
    const today = startOfDay(now).getTime();
    const minDate = today - config.overdueWindowDays * DAY_MS;
    const maxDate = today + config.lookAheadDays * DAY_MS;
    const maxItems = max ?? config.maxShown;

    return transactions
        .filter((transaction) => {
            if ((transaction.type ?? "expense") !== "expense") return false;
            if (transaction.liquidityPoolId) return false;
            if (transaction.invoiceImport?.importId) return false;
            const timestamp = toTimestamp(transaction.date);
            if (!timestamp) return false;
            const day = startOfDay(new Date(timestamp)).getTime();
            return day >= minDate && day <= maxDate;
        })
        .map((transaction) => ({
            transaction,
            dueDays: daysUntil(transaction.date, now),
        }))
        .map((candidate) => ({
            ...candidate,
            score: rankPersonalPayment(candidate.transaction, candidate.dueDays, config),
        }))
        .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            const dateDiff = toTimestamp(a.transaction.date) - toTimestamp(b.transaction.date);
            if (dateDiff !== 0) return dateDiff;
            return (Number(b.transaction.amount) || 0) - (Number(a.transaction.amount) || 0);
        })
        .slice(0, maxItems)
        .map(({ transaction, dueDays }) => {
            const currency = transaction.currency || "ARS";
            const amount = formatCurrencyAmountOrMasked(Number(transaction.amount) || 0, currency, balancesVisible, locale);
            const isOverdue = dueDays < 0;
            return {
                id: `personal-upcoming-${transaction._id}`,
                scope: "personal",
                tone: isOverdue || dueDays <= 3 ? "warning" : "info",
                title: humanPaymentTitle(dueDays, locale, transaction.name),
                body: isOverdue ? amount : `${transaction.name} · ${amount}`,
                meta: new Date(toTimestamp(transaction.date)).toLocaleDateString(locale, { day: "numeric", month: "short" }),
                date: new Date(toTimestamp(transaction.date)).toISOString(),
                actionLabel: isSpanishLocale(locale) ? "Ver en calendario" : "View calendar",
                sourceId: transaction._id,
            } satisfies AppNotification;
        });
}

function activePeople(people: UserEntry[] = []): UserEntry[] {
    return people.filter((person) => person.active !== false);
}

function resolveCurrentParticipantId(username: string, userId: string | undefined, people: UserEntry[]): string {
    const current = activePeople(people).find((person) => (
        person.userId === userId ||
        person.id === userId ||
        person.username === username ||
        (!person.userId && person.id === username)
    ));
    return current?.id ?? userId ?? username;
}

function personName(people: UserEntry[], personId: string | undefined, fallback: string): string {
    return people.find((person) => person.id === personId)?.displayName || fallback;
}

function settlementCreditor(tx: GroupTransaction): string | undefined {
    return Object.keys(tx.splitDetails ?? {})[0];
}

function buildGroupNotification({
    group,
    tx,
    currentParticipantId,
    balancesVisible,
    locale,
}: {
    group: GroupSummary;
    tx: GroupTransaction;
    currentParticipantId: string;
    balancesVisible: boolean;
    locale: string;
}): AppNotification | null {
    const people = group.people ?? [];
    const spanish = isSpanishLocale(locale);
    const timestamp = toTimestamp(tx.date);
    if (!timestamp) return null;

    const currency = tx.currency || "ARS";
    const payerName = personName(people, tx.payer, spanish ? "Alguien" : "Someone");
    const amount = formatCurrencyAmountOrMasked(Number(tx.amount) || 0, currency, balancesVisible, locale);
    const meta = `${group.name} · ${new Date(timestamp).toLocaleDateString(locale, { day: "numeric", month: "short" })}`;

    if (tx.type === "settlement") {
        const creditor = settlementCreditor(tx);
        const creditorName = personName(people, creditor, spanish ? "alguien" : "someone");
        if (creditor === currentParticipantId) {
            return {
                id: `${group._id}-${tx._id}-settlement-in`,
                scope: "group",
                tone: "success",
                title: spanish ? `${payerName} te pagó ${amount}` : `${payerName} paid you ${amount}`,
                body: tx.name,
                meta,
                date: new Date(timestamp).toISOString(),
                sourceId: tx._id,
            };
        }
        if (tx.payer === currentParticipantId) {
            return {
                id: `${group._id}-${tx._id}-settlement-out`,
                scope: "group",
                tone: "info",
                title: spanish ? `Le pagaste ${amount} a ${creditorName}` : `You paid ${creditorName} ${amount}`,
                body: tx.name,
                meta,
                date: new Date(timestamp).toISOString(),
                sourceId: tx._id,
            };
        }
        return {
            id: `${group._id}-${tx._id}-settlement`,
            scope: "group",
            tone: "info",
            title: spanish ? `${payerName} le pagó a ${creditorName}` : `${payerName} paid ${creditorName}`,
            body: amount,
            meta,
            date: new Date(timestamp).toISOString(),
            sourceId: tx._id,
        };
    }

    const userShare = Number(tx.splitDetails?.[currentParticipantId] ?? 0);
    const youPaid = tx.payer === currentParticipantId;
    const creatorId = tx.createdBy ?? tx.payer;
    const creatorName = personName(people, creatorId, spanish ? "Alguien" : "Someone");
    const youCreated = creatorId === currentParticipantId;
    const title = youCreated
        ? (spanish ? `Cargaste ${tx.name}` : `You added ${tx.name}`)
        : (spanish ? `${creatorName} cargó ${tx.name}` : `${creatorName} added ${tx.name}`);

    const showPaidBy =
        tx.payer !== creatorId &&
        currentParticipantId !== tx.payer;
    const paidByPrefix = showPaidBy
        ? (spanish ? `Pago ${payerName} · ` : `Paid by ${payerName} · `)
        : "";

    const body = youPaid
        ? paidByPrefix + (spanish ? `Total anotado: ${amount}` : `Recorded total: ${amount}`)
        : userShare > 0
            ? paidByPrefix + (spanish
                ? `Tu parte: ${formatCurrencyAmountOrMasked(userShare, currency, balancesVisible, locale)}`
                : `Your share: ${formatCurrencyAmountOrMasked(userShare, currency, balancesVisible, locale)}`)
            : paidByPrefix + (spanish ? `No participaste · Total: ${amount}` : `You were not included · Total: ${amount}`);

    return {
        id: `${group._id}-${tx._id}`,
        scope: "group",
        tone: youPaid ? "success" : userShare > 0 ? "warning" : "info",
        title,
        body,
        meta,
        date: new Date(timestamp).toISOString(),
        sourceId: tx._id,
    };
}

export function buildGroupActivityNotifications({
    groups,
    username,
    userId,
    balancesVisible,
    locale,
    max = 3,
    groupId,
    now = new Date(),
}: {
    groups: GroupSummary[];
    username: string;
    userId?: string;
    balancesVisible: boolean;
    locale: string;
    max?: number;
    groupId?: string;
    now?: Date;
}): AppNotification[] {
    const cutoff = now.getTime() - GROUP_NOTIFICATION_WINDOW_DAYS * DAY_MS;
    type Row = { notification: AppNotification; score: number; at: number };
    const rows: Row[] = [];

    for (const group of groups) {
        if (groupId && group._id !== groupId) continue;
        const currentParticipantId = resolveCurrentParticipantId(username, userId, group.people ?? []);
        for (const tx of group.transactions ?? []) {
            const timestamp = toTimestamp(tx.date);
            if (!timestamp || timestamp < cutoff) continue;
            const notification = buildGroupNotification({
                group,
                tx,
                currentParticipantId,
                balancesVisible,
                locale,
            });
            if (!notification) continue;
            const score = rankGroupActivity(tx, group, {
                currentParticipantId,
                now,
                windowDays: GROUP_NOTIFICATION_WINDOW_DAYS,
            });
            rows.push({ notification, score, at: timestamp });
        }
    }

    return rows
        .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            if (b.at !== a.at) return b.at - a.at;
            return a.notification.id.localeCompare(b.notification.id);
        })
        .slice(0, max)
        .map((r) => r.notification);
}
